export type IdlePhase = 'active' | 'warning' | 'expired'

export const DEFAULT_IDLE_MINUTES = 30
export const IDLE_WARNING_MS = 2 * 60_000
export const ACTIVITY_WRITE_INTERVAL_MS = 10_000
export const HEARTBEAT_INTERVAL_MS = 5 * 60_000
export const LAST_ACTIVITY_KEY = 'auth:last_activity'
export const IDLE_TIMEOUT_KEY = 'auth:idle_timeout'

const ANNOUNCE_AT_SECONDS = [10, 30, 60, 120] as const

/** EMR's `user_idle_timeout`; null means nobody chose and nothing is enforced, which EMR treats as 30. */
export function idleTimeoutMs(minutes: unknown): number {
  if (typeof minutes === 'number' && Number.isFinite(minutes) && minutes > 0) {
    return minutes * 60_000
  }
  return DEFAULT_IDLE_MINUTES * 60_000
}

export function idlePhase(lastActivity: number, now: number, timeoutMs: number): IdlePhase {
  const deadline = lastActivity + timeoutMs
  if (now >= deadline) return 'expired'
  if (now >= deadline - IDLE_WARNING_MS) return 'warning'
  return 'active'
}

export function msUntilNextPhase(lastActivity: number, now: number, timeoutMs: number): number {
  const deadline = lastActivity + timeoutMs
  const warningAt = deadline - IDLE_WARNING_MS
  return Math.max(0, (now < warningAt ? warningAt : deadline) - now)
}

export function readLastActivity(now: number): number | null {
  try {
    const stamp = Number(localStorage.getItem(LAST_ACTIVITY_KEY))
    return Number.isFinite(stamp) && stamp > 0 && stamp <= now ? stamp : null
  } catch {
    return null
  }
}

export function writeLastActivity(now: number): void {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now))
  } catch {
    // Storage refused the write. The composable keeps an in-memory copy for this tab.
  }
}

export function writeIdleTimeout(minutes: number): void {
  try {
    localStorage.setItem(IDLE_TIMEOUT_KEY, String(minutes))
  } catch {
    // Other tabs pick the new timeout up on their next reload instead.
  }
}

export function clearIdleStorage(): void {
  try {
    localStorage.removeItem(LAST_ACTIVITY_KEY)
    localStorage.removeItem(IDLE_TIMEOUT_KEY)
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

export function formatCountdown(ms: number): string {
  const seconds = Math.max(0, Math.ceil(ms / 1000))
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

/** A live region re-reads on every change, so it only changes at these marks. */
export function announcedSeconds(ms: number): number {
  const seconds = Math.ceil(ms / 1000)
  return ANNOUNCE_AT_SECONDS.find((mark) => mark >= seconds) ?? 120
}
